/**
 * The application shell: a top navbar over a centred content column.
 *
 * Ported from `design_system/` conventions — daisyUI `navbar`, `menu`,
 * `dropdown`, `breadcrumbs` — rather than from any single template page, since
 * the template is sidebar-first and Picklii has few enough destinations that a
 * navbar carries them without a sidebar's overhead. ADR-0008, ADR-0011.
 *
 * A drawer *is* now used — for the agent panel (SPEC-0004), opening from the
 * right via `drawer-end` — so the note below is load-bearing rather than
 * historical: daisyUI's drawer selects with direct child combinators
 * (`.drawer-toggle:checked ~ .drawer-side`), so the toggle, content, and side
 * must all be *real* DOM children of the `.drawer` element. Mounting React into
 * a wrapper in between silently breaks it — `display: contents` changes layout,
 * not selector matching — and the panel renders nothing at all with no error.
 * Keep those three as literal siblings below.
 */
import { useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { AgentPanel } from './AgentPanel';
import { Icon, type IconName } from './Icon';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';
import { useAuth } from '../lib/auth';

const NAV: ReadonlyArray<{ to: string; label: string; icon: IconName; end?: boolean }> = [
	{ to: '/', label: 'Dashboard', icon: 'dashboard', end: true },
	{ to: '/runs', label: 'Reconciliations', icon: 'reconcile' },
	{ to: '/profiles', label: 'Mapping profiles', icon: 'mapping' },
	{ to: '/settings', label: 'Settings', icon: 'settings' },
];

const linkClass = ({ isActive }: { isActive: boolean }) => (isActive ? 'menu-active' : '');

function NavItems() {
	return (
		<>
			{NAV.map((item) => (
				<li key={item.to}>
					<NavLink to={item.to} end={item.end} className={linkClass}>
						<Icon name={item.icon} />
						{item.label}
					</NavLink>
				</li>
			))}
		</>
	);
}

export function AppShell({
	title,
	breadcrumbs,
	actions,
	children,
}: {
	title: string;
	breadcrumbs?: ReactNode;
	actions?: ReactNode;
	children: ReactNode;
}) {
	const { user, signOutNow } = useAuth();
	const navigate = useNavigate();

	// The agent opens over whatever page is mounted, and that page is never
	// unmounted — it is consulted *about* what is on screen. SPEC-0004 §1.2.
	const [agentOpen, setAgentOpen] = useState(false);

	return (
		/*
		 * `drawer-open` is what makes the panel *push* the page aside rather than
		 * float over it: it swaps `.drawer-side` from `position: fixed` to
		 * `sticky` and gives it a real column in the drawer's grid, so the content
		 * column genuinely narrows. Toggling the class is therefore the open/close
		 * mechanism, and the checkbox below only mirrors React's state — daisyUI
		 * sets `display: none` on the toggle whenever `drawer-open` is present.
		 *
		 * It also means there is no backdrop to click: daisyUI makes the overlay
		 * transparent and `cursor: default` in this mode, which is why the overlay
		 * element is gone entirely and Escape plus the close control are the ways
		 * out. SPEC-0004 §1.3.
		 */
		<div className={`drawer drawer-end${agentOpen ? ' drawer-open' : ''}`}>
			<input
				id="agent-drawer"
				type="checkbox"
				className="drawer-toggle"
				checked={agentOpen}
				onChange={(event) => setAgentOpen(event.target.checked)}
				aria-label="Reconciliations Agent"
			/>

			<div className="drawer-content min-h-screen">
				{/*
				 * `@container` — not a viewport breakpoint — is what keeps this bar
				 * intact while the agent pushes the page. daisyUI gives `navbar-start`
				 * and `navbar-end` `width: 50%` each and `navbar-center`
				 * `flex-shrink: 0`, so the three only fit while they have the full
				 * column. A `lg:` breakpoint asks the *window* how wide it is, which
				 * stays 1440px while this column drops to 1024 − the panel's 26rem, and
				 * the three children then overflow their boxes and overlap each other.
				 *
				 * The container variants below ask this header instead, so the
				 * destinations collapse into the dropdown the moment the column — not
				 * the window — runs out of room.
				 *
				 * 71rem is measured, and two things about that measurement are easy to
				 * get wrong.
				 *
				 * First, a container query sizes against the container's CONTENT box,
				 * so this figure must exclude the header's own `px-4 lg:px-8`. Add the
				 * padding in and the bar collapses ~64px later than intended: at a
				 * 1210px header the query sees 1146.
				 *
				 * Second, the account e-mail is taken from its `max-w-40` truncation
				 * cap, not from a live measurement. Addresses are unbounded; 160px is
				 * the design constant. Deriving it from a short dev address makes the
				 * actions group look 114px narrower than it can be, which left about
				 * 5px of gap for anyone whose address actually reached the cap — the
				 * crowding this whole comment exists to prevent.
				 *
				 * So: 1062px of content (66 wordmark + 541 destinations + 455 actions
				 * at the cap) plus a 32px gap either side = 1126px. 71rem is 1136,
				 * which leaves 37px of gap at the boundary. Re-measure if the bar
				 * gains an item.
				 *
				 * `container-type: inline-size` implies `contain: layout style
				 * inline-size` but *not* paint, so the dropdowns below still overflow
				 * this element visually; and they anchor to their own `position:
				 * relative` `.dropdown` parents, not to this new containing block.
				 *
				 * The bar has two layout modes, because centring the destinations and
				 * fitting them cannot both hold at every width.
				 *
				 * Centring on the bar requires each side track to be half of what is
				 * left over, and the actions group is far wider than the wordmark — so
				 * the binding constraint is (content − 541) / 2 ≥ 455 + 24 of gap, i.e.
				 * 1499px. Above that, `@min-[94rem]:flex-1` makes the two side tracks
				 * equal, which puts the centre group's midpoint exactly on the bar's,
				 * measured at 0px off with the widest possible account name.
				 *
				 * Below it, the tracks stay content-sized and `justify-between` spreads
				 * them instead. The destinations are then off-centre — about 195px at a
				 * 1226px bar — but evenly spaced and, crucially, not overflowing.
				 * Forcing `flex-1` all the way down does centre at every width, and was
				 * tried: the actions group simply overruns its track and spills 24px
				 * past the header's padding.
				 *
				 * daisyUI's own `width: 50%` centres on the bar too, and this is where
				 * it fails — it reserves nothing, so once the actions group outgrew half
				 * the bar the destinations were shoved against it, 278px of space before
				 * them and 0 after. `gap-6` is what makes the fallback degrade evenly
				 * rather than jam: 24px either side is reserved before any slack is
				 * handed out.
				 *
				 * Verified at the worst-case account name: 1506 → centred, gaps 417/28;
				 * 1226 → 195 off-centre, gaps 82/82; 1141 → gaps 40/40; 1086 → dropdown.
				 * No overflow at any of them.
				 */}
				<header className="navbar bg-base-200 border-base-300 @container justify-between gap-6 border-b px-4 lg:px-8">
					<div className="navbar-start w-auto gap-2 @min-[94rem]:flex-1">
						{/* Narrow columns collapse the destinations into a dropdown. */}
						<div className="dropdown @min-[71rem]:hidden">
							{/*
							 * The class goes on the trigger, not around it: daisyUI's dropdown
							 * resolves its content as a sibling of the trigger, so a wrapper
							 * between the two breaks that relationship.
							 *
							 * `tooltip-right`, not `tooltip-bottom`, because the dropdown opens
							 * downward from this same button. daisyUI fires a tooltip on
							 * `:hover`, and the pointer is still on the trigger the moment the
							 * menu appears, so a bottom tip lands exactly where the menu is —
							 * behind it, since the menu's `z-10` beats the tooltip's `z-2`,
							 * poking out as a stray sliver at the edge. Sideways stays clear.
							 */}
							<button
								tabIndex={0}
								className="btn btn-square btn-ghost btn-sm tooltip tooltip-right"
								data-tip="Menu"
								aria-label="Open menu">
								<Icon name="menu" className="size-5" />
							</button>
							<ul
								tabIndex={0}
								className="menu dropdown-content rounded-box bg-base-200 z-10 mt-3 w-60 p-2 shadow-2xl">
								<NavItems />
							</ul>
						</div>

						<NavLink to="/" className="btn btn-ghost px-2">
							<Logo />
						</NavLink>
					</div>

					<div className="navbar-center hidden @min-[71rem]:flex">
						{/*
						 * `py-0` is load-bearing, not tidiness. `.menu` carries 0.5rem of
						 * its own padding on every side, and `px-1` only replaces the
						 * horizontal half — leaving this the tallest thing in the bar at
						 * 49px, which pushed the header to 66px and past daisyUI's 4rem
						 * `min-height`. The agent panel's header has no such child and
						 * sits at exactly 4rem, so the two bottom borders met two pixels
						 * apart where the panel adjoins the bar. Zeroed, the floor governs
						 * on both sides and they line up. The items are already centred,
						 * so nothing moves but the height.
						 */}
						<ul className="menu menu-horizontal gap-1 px-1 py-0">
							<NavItems />
						</ul>
					</div>

					{/*
					 * Every control in this bar is a daisyUI `menu` item, so hover, focus
					 * and active states are the same styling as the destinations on the
					 * left — identical by construction rather than by copying values that
					 * would drift apart the next time either changed.
					 *
					 * None of them may carry `btn`. daisyUI's menu-hover selector ends in
					 * `:not(.menu-active, :active, .btn)`, so a `btn btn-ghost` control
					 * inside a menu silently opts *out* of the menu hover and falls back
					 * to the ghost button's, which is a near-black tint that is almost
					 * invisible on this navbar. That mismatch is exactly what these
					 * wrappers exist to prevent.
					 *
					 * The dropdown stays outside the `ul` and wraps its own trigger in a
					 * menu instead: a `dropdown-content` nested directly inside a menu
					 * `li` would be styled as a *submenu* — indented, with a rule down its
					 * inline edge — rather than as the floating panel it is.
					 */}
					<div className="navbar-end w-auto gap-2 @min-[94rem]:flex-1">
						{/*
						 * Tinted primary while inactive so the one action in the bar still
						 * reads as the action. The tint is dropped when active so the item
						 * matches every other active destination exactly — `menu-active`
						 * sets its own colour, and leaving a competing utility on it would
						 * make which one wins a question of specificity rather than intent.
						 *
						 * It is also why the dark theme's primary is lighter than the light
						 * theme's: the brand sage at its light-theme lightness measures
						 * 2.34:1 as text on a dark navbar. Same hue, lifted for the surface.
						 */}
						{/* Tooltipped because the label below 40rem is gone and this becomes
						    an icon on its own. Above it the tip repeats the label, which is
						    redundant but harmless — the alternative is an unnamed icon. */}
						<div className="tooltip tooltip-bottom" data-tip="New reconciliation">
							<ul className="menu menu-horizontal p-0">
								<li>
									<NavLink
										to="/runs/new"
										className={({ isActive }) => (isActive ? 'menu-active' : 'text-primary')}>
										<Icon name="upload" />
										{/* 40rem is the `sm` breakpoint this replaces; it is not on
										    Tailwind's named container scale, hence the arbitrary value. */}
										<span className="hidden @min-[40rem]:inline">New reconciliation</span>
									</NavLink>
								</li>
							</ul>
						</div>

						{/*
						 * The tooltip wraps the `ul`, never the control inside it. daisyUI
						 * lays a menu item's child out as a grid
						 * (`li:not(.menu-title) > :not(ul, menu, details, .menu-title, .btn)`)
						 * and `.tooltip` carries `display: inline-block`, so putting the
						 * class on the control itself trades the item's layout for the
						 * tooltip. Wrapping the single-item menu keeps both.
						 */}
						<div className="tooltip tooltip-bottom" data-tip="Switch theme">
							<ul className="menu menu-horizontal p-0">
								<li>
									<ThemeToggle />
								</li>
							</ul>
						</div>

						<div className="dropdown dropdown-end">
							<ul className="menu menu-horizontal p-0">
								<li>
									<button tabIndex={0} aria-label="Account menu">
										<span className="max-w-40 truncate">{user?.email ?? 'Account'}</span>
									</button>
								</li>
							</ul>
							<ul
								tabIndex={0}
								className="menu dropdown-content rounded-box bg-base-200 z-10 mt-3 w-56 p-2 shadow-2xl">
								<li className="menu-title">{user?.email}</li>
								<li>
									<button
										onClick={async () => {
											await signOutNow();
											navigate('/sign-in');
										}}>
										<Icon name="logout" />
										Sign out
									</button>
								</li>
							</ul>
						</div>

						{/*
						 * Last in the bar, after the account. Reachable from every
						 * authenticated page so the agent is there wherever the question
						 * occurs to someone. SPEC-0004 §1.1.
						 *
						 * Gone once the panel is open: the panel carries its own close
						 * control, so leaving this here would offer a second control for
						 * the same state — and, since it only ever opens, one that does
						 * nothing when pressed. It also gives the narrowed bar back the
						 * width. Unmounting it strands no focus: the panel moves focus to
						 * its composer on open.
						 */}
						{!agentOpen && (
							<div className="tooltip tooltip-left" data-tip="Reconciliations Agent">
								<ul className="menu menu-horizontal p-0">
									<li>
										<button
											onClick={() => setAgentOpen(true)}
											aria-label="Open the Reconciliations Agent">
											{/*
											 * `size-4` must be repeated here: `Icon`'s className is a
											 * default parameter, so any value passed REPLACES the
											 * default rather than adding to it. Dropping it leaves the
											 * svg with no intrinsic size and it collapses to nothing.
											 */}
											<Icon name="sparkles" className="text-primary size-4" />
										</button>
									</li>
								</ul>
							</div>
						)}
					</div>
				</header>

				<main className="mx-auto w-full max-w-[100rem] p-4 lg:p-8">
					<div className="grid grid-cols-12 gap-6">
						<div className="col-span-12 flex flex-col gap-4 lg:flex-row lg:items-end">
							<div className="grow">
								{breadcrumbs ?? (
									<div className="breadcrumbs text-sm">
										<ul>
											<li>Picklii</li>
											<li>
												<h1>{title}</h1>
											</li>
										</ul>
									</div>
								)}
							</div>
							{actions !== undefined && <div className="flex flex-wrap gap-2">{actions}</div>}
						</div>

						{children}
					</div>
				</main>
			</div>

			{/*
			 * A real sibling of the toggle and the content — see the note at the top
			 * of this file. There is deliberately no `drawer-overlay` child: this
			 * drawer pushes rather than floats, so there is nothing to dim and
			 * nothing behind it to click through to.
			 */}
			<div className="drawer-side z-30">
				<AgentPanel open={agentOpen} onClose={() => setAgentOpen(false)} />
			</div>
		</div>
	);
}
